import React, { useState, useEffect, useMemo } from 'react';
import {
    makeStyles, tokens,
    Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
    Button, Spinner, Input, Field, Dropdown, Option, Text,
    RadioGroup, Radio,
} from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import CellItem from '../components/CellItem';
import ItemViewDialog from '../dialogs/ItemViewDialog'; // Įsitikinkite dėl kelio
import { API_BASE, API_HOST } from '../../config.js';

const CloseIcon = iconMap.close;

// Module-level cache — persists for the lifetime of the browser session
const _apiCache = {};

// MDF Kainų/Apdailos žemėlapis
const MDF_FINISHES = [
    { label: "Matinis", key: "price_matte_2_side" },
    { label: "Matinis (galas baltas melaminas)", key: "price_matte_1_side" },
    { label: "Blizgus", key: "price_shiny_1_side" },
    { label: "Supermatinis", key: "price_super_shiny_1_side" },
    { label: "Superblizgus", key: "price_super_shiny_2_side" }
];

const useStyles = makeStyles({
    surface: { maxWidth: '1400px', width: '100%' },
    body: { minHeight: 0 },
    dialogTitle: { borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
    // Kairė filtrų juosta + dešinė rezultatų kolona (ne viena virš kitos horizontali
    // filtrų eilutė) — anksčiau 8 filtrų laukai vienoje eilutėje spausdavo dialogą
    // platyn ir sukeldavo horizontalų scrollbar'ą (žr. /admin/bugs "Cia neturetu buti
    // jokiu horizontaliu scrollbar... gal kaireje puseje filtra padarayti?").
    // maxHeight 75vh→85vh — po to, kai LMDP filtrai nustojo būti Accordion viduje
    // (visada išskleisti, žr. žemiau), kairė juosta pasidarė aukštesnė ir kartais
    // gaudavo savo vidinį scrollbar'ą be reikalo (žr. /admin/bugs "Sumazink sita
    // bloka arba praplesk bet apacioje scrollbar neturi buti").
    content: { display: 'flex', flexDirection: 'row', minHeight: 0, maxHeight: '85vh', padding: 0, overflow: 'hidden' },
    sidebar: {
        // 200px→240px (žr. /admin/bugs "Praplesti sia bloka ir visi input ar
        // select turi buti tokio pacio plocio") — filtrų laukai (visi jau
        // width:100% per filterField žemiau, tad jau vienodo pločio) turėjo
        // per mažai vietos tekstui, dabar platesni.
        width: '240px', flexShrink: 0, overflowY: 'auto',
        borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
        // 20px visose pusėse (žr. /admin/bugs "Visi filtro laukai turi buti vienodo
        // plocio ir 20px nutole nuo desiniojo border") — filterField žemiau užpildo
        // visą (width:100%) šitą paddingu apribotą plotį, tad kiekvienas laukas
        // baigiasi lygiai 20px iki sidebar dešiniojo krašto, o visi laukai (paieška
        // + kiekvienas dropdown'as) tampa VIENODO pločio.
        padding: tokens.spacingHorizontalXL,
        display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    },
    typeSwitch: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
    resultsCol: { flex: 1, minWidth: 0, overflowY: 'auto', padding: tokens.spacingHorizontalL },
    resultsCount: { display: 'block', marginBottom: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
    filterField: { width: '100%' },
    centerBox: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL },
    errorBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalM, padding: tokens.spacingVerticalXXXL },
    errorText: { color: tokens.colorPaletteRedForeground1, textAlign: 'center' },
    // CSS grid + repeat(auto-fit,...) su fiksuoto pločio SelectionBox (type=column,
    // justifySelf:start) palikdavo tuščius grid langelius kaip pilno pločio "tarpus",
    // kai stulpelių daugiau nei realių elementų (žr. /admin/bugs "Ta pati grid
    // problema, nenaudojam grid" — bendra taisyklė visiems dialogams). flex+wrap+gap
    // vietoj grid — kiekvienas elementas užima tik savo turinio plotį.
    resultsGrid: {
        display: 'flex', flexWrap: 'wrap', gap: '10px',
    },
    emptyResult: { width: '100%', textAlign: 'center', color: tokens.colorNeutralForeground3, display: 'block' },
});

const LMDP_FILTER_FIELDS = [
    { key: 'supplier', label: 'Tiekėjas' },
    { key: 'color_tone', label: 'Spalvos tonas' },
    { key: 'color', label: 'Spalva' },
    { key: 'gloss', label: 'Blizgumas' },
    { key: 'roughness', label: 'Grublėtumas' },
    { key: 'pattern', label: 'Raštas' },
    { key: 'manufacturer', label: 'Gamintojas' },
];

const PanelSelectingDialog = ({
    open,
    onClose,
    panelType: initialPanelType,
    onItemSelect,
    currentPlate,
}) => {
    const s = useStyles();
    const [panels, setPanels] = useState([]); // LMDP data
    const [mdfList, setMdfList] = useState([]); // MDF data
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [activeType, setActiveType] = useState(initialPanelType || 0); // 0: LMDP, 1: MDF
    const [loadedTypes, setLoadedTypes] = useState(new Set());
    const [selectedCode, setSelectedCode] = useState(null);

    // Peržiūros dialogas
    const [viewItem, setViewItem] = useState(null);
    const [isViewOpen, setIsViewOpen] = useState(false);

    // --- FILTRAI ---
    const [searchTerm, setSearchTerm] = useState("");

    // MDF filtrai
    const [mdfFinish, setMdfFinish] = useState(MDF_FINISHES[0].key); // Default: Matinis

    // LMDP filtrai
    const [lmdpFilters, setLmdpFilters] = useState({
        supplier: '',
        color_tone: '',
        color: '',
        gloss: '',
        roughness: '',
        pattern: '',
        manufacturer: ''
    });

    // --- DATA FETCHING ---
    const fetchPanels = async () => {
        if (_apiCache.panels) {
            setPanels(_apiCache.panels);
            setLoadedTypes(prev => new Set([...prev, 0]));
            return;
        }
        try {
            setLoading(true);
            setFetchError(null);
            const response = await fetch(`${API_BASE}/panels`);
            if (!response.ok) throw new Error(`Serverio klaida (${response.status})`);
            const data = await response.json();
            _apiCache.panels = data;
            setPanels(data);
            setLoadedTypes(prev => new Set([...prev, 0]));
        } catch (error) {
            console.error(error);
            setFetchError(error.message || 'Nepavyko gauti plokščių sąrašo');
        } finally {
            setLoading(false);
        }
    };

    const fetchMdf = async () => {
        if (_apiCache.mdf) {
            setMdfList(_apiCache.mdf);
            setLoadedTypes(prev => new Set([...prev, 1]));
            return;
        }
        try {
            setLoading(true);
            setFetchError(null);
            const response = await fetch(`${API_BASE}/mdf`);
            if (!response.ok) throw new Error(`Serverio klaida (${response.status})`);
            const data = await response.json();
            _apiCache.mdf = data;
            setMdfList(data);
            setLoadedTypes(prev => new Set([...prev, 1]));
        } catch (error) {
            console.error(error);
            setFetchError(error.message || 'Nepavyko gauti MDF sąrašo');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            const type = initialPanelType ?? 0;
            setActiveType(type);
            setSelectedCode(currentPlate?.code || null);
            if (type === 0 && !loadedTypes.has(0)) fetchPanels();
            else if (type === 1 && !loadedTypes.has(1)) fetchMdf();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialPanelType]);

    // Handle type switching manually inside dialog
    const handleTypeSwitch = (type) => {
        setActiveType(type);
        if (type === 0 && !loadedTypes.has(0)) fetchPanels();
        if (type === 1 && !loadedTypes.has(1)) fetchMdf();
    };

    // --- HELPERS FOR IMAGES ---
    const getImagePath = (item, type) => {
        if (!item.image_1 && !item.image) return null;
        const imageName = item.image_1 || item.image;
        if (imageName.startsWith('http')) return imageName;
        return type === 0 ? `${API_HOST}/lmdp/${imageName}` : `${API_HOST}/mdf/${imageName}`;
    };

    // --- FILTRAVIMO LOGIKA ---

    // Helper: Gauti unikalias reikšmes dropdown'ams
    const getUniqueOptions = (data, key) => {
        return [...new Set(data.map(item => item[key]).filter(val => val !== undefined && val !== null && val !== ''))].sort();
    };

    const filteredItems = useMemo(() => {
        let data = activeType === 0 ? panels : mdfList;

        // 1. Search Filter
        if (searchTerm) {
            const lower = searchTerm.toLowerCase();
            data = data.filter(item =>
                (item.code && item.code.toLowerCase().includes(lower)) ||
                (item.name && item.name.toLowerCase().includes(lower))
            );
        }

        // 2. LMDP Specific Filters
        if (activeType === 0) {
            Object.keys(lmdpFilters).forEach(key => {
                if (lmdpFilters[key]) {
                    data = data.filter(item => item[key] === lmdpFilters[key]);
                }
            });
        }

        return data;
    }, [activeType, panels, mdfList, searchTerm, lmdpFilters]);


    // --- HANDLERS ---

    const handleFilterChange = (key, value) => {
        setLmdpFilters(prev => ({ ...prev, [key]: value }));
    };

    // Pasirinkti elementą
    const handleItemSelect = (item) => {
        setSelectedCode(item.code);
        // panelType (0=LMDP/1=MDF) PRIVALO būti ant pačio objekto — žaliavinis
        // katalogo įrašas (iš /panels arba /mdf) šio lauko NETURI (skiriasi tik
        // pagal TAI, iš kurio endpoint'o atėjo). Anksčiau panelType buvo perduodamas
        // TIK kaip antras onItemSelect(selectedData, activeType) argumentas — bet
        // kai kurie kvietėjai (žr. PlatesFillingsAndHandles.jsx onPlateItemSelect)
        // priima TIK pirmą argumentą, tad plokštė išsisaugodavo BE panelType. Vėliau
        // jokia `plate.panelType === 0`/`=== 1` sąlyga nesutapdavo, tad nei
        // Veidrodis, nei Filingas langelis nebepiešdavosi iš viso (žr. ClickUp "3.0
        // Išorė. Meniu - Pakeitus plokštę nebeleidžia rinktis veidrodžio/filingų").
        // Priskiriant TIESIOGIAI ant objekto, jis savaime teisingas nepriklausomai
        // nuo to, ar kvietėjas naudoja antrą argumentą.
        let selectedData = { ...item, panelType: activeType };

        if (activeType === 1) {
            // MDF: Pridedame pasirinktą apdailą ir kainą
            const finishLabel = MDF_FINISHES.find(f => f.key === mdfFinish)?.label;
            selectedData.selectedFinishCode = mdfFinish;
            selectedData.selectedFinishLabel = finishLabel;
            selectedData.price = item[mdfFinish];
        }

        if (onItemSelect) {
            onItemSelect(selectedData, activeType);
        }
        onClose();
    };

    // Peržiūrėti elementą (atidaryti ItemViewDialog)
    const handleItemView = (item) => {
        setViewItem(item);
        setIsViewOpen(true);
    };

    return (
        <>
            <Dialog
                open={open}
                onOpenChange={(_e, data) => { if (!data.open) onClose(); }}
            >
                <DialogSurface id="dialog-panel-selecting" className={s.surface}>
                    <DialogBody className={s.body}>
                        <DialogTitle
                            className={s.dialogTitle}
                            action={<Button appearance="subtle" icon={<CloseIcon />} aria-label="Uždaryti" onClick={onClose} />}
                        >
                            Pasirinkite plokštę
                        </DialogTitle>
                        <DialogContent className={s.content}>
                            {/* KAIRĖ — plokštės tipas + visi filtrai */}
                            <div className={s.sidebar}>
                                <RadioGroup
                                    className={s.typeSwitch}
                                    value={String(activeType)}
                                    onChange={(_e, data) => handleTypeSwitch(Number(data.value))}
                                >
                                    <Radio value="0" label="LMDP" />
                                    <Radio value="1" label="MDF" />
                                </RadioGroup>

                                <Field label="Paieška (Kodas/Pavadinimas)" className={s.filterField}>
                                    <Input
                                        size="small"
                                        className={s.filterField}
                                        value={searchTerm}
                                        onChange={(_e, data) => setSearchTerm(data.value)}
                                    />
                                </Field>

                                {/* MDF Specifiniai filtrai */}
                                {activeType === 1 && (
                                    <Field label="Blizgumas / Apdaila" className={s.filterField}>
                                        <Dropdown
                                            size="small"
                                            className={s.filterField}
                                            value={MDF_FINISHES.find(opt => opt.key === mdfFinish)?.label || ''}
                                            selectedOptions={[mdfFinish]}
                                            onOptionSelect={(_e, data) => setMdfFinish(data.optionValue)}
                                        >
                                            {MDF_FINISHES.map(opt => (
                                                <Option key={opt.key} value={opt.key}>{opt.label}</Option>
                                            ))}
                                        </Dropdown>
                                    </Field>
                                )}

                                {/* LMDP Specifiniai filtrai — visada matomi, be Accordion suskleidimo
                                    (žr. /admin/bugs "Nereikia siame filtravime accordion"). */}
                                {activeType === 0 && LMDP_FILTER_FIELDS.map(({ key: filterKey, label }) => (
                                    <Field key={filterKey} label={label} className={s.filterField}>
                                        <Dropdown
                                            size="small"
                                            className={s.filterField}
                                            value={lmdpFilters[filterKey] || 'Visi'}
                                            selectedOptions={lmdpFilters[filterKey] ? [lmdpFilters[filterKey]] : []}
                                            onOptionSelect={(_e, data) => handleFilterChange(filterKey, data.optionValue ?? '')}
                                        >
                                            <Option value="">Visi</Option>
                                            {getUniqueOptions(panels, filterKey).map(val => (
                                                <Option key={val} value={val}>{val}</Option>
                                            ))}
                                        </Dropdown>
                                    </Field>
                                ))}
                            </div>

                            {/* DEŠINĖ — rezultatai */}
                            <div className={s.resultsCol}>
                                {loading ? (
                                    <div className={s.centerBox}><Spinner size="large" /></div>
                                ) : fetchError ? (
                                    <div className={s.errorBox}>
                                        <Text className={s.errorText}>{fetchError}</Text>
                                        <Button appearance="outline" onClick={() => {
                                            if (activeType === 0) { delete _apiCache.panels; setLoadedTypes(p => { const set = new Set(p); set.delete(0); return set; }); fetchPanels(); }
                                            else { delete _apiCache.mdf; setLoadedTypes(p => { const set = new Set(p); set.delete(1); return set; }); fetchMdf(); }
                                        }}>
                                            Bandyti dar kartą
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <Text className={s.resultsCount}>{filteredItems.length} rezultatai</Text>
                                        <div className={s.resultsGrid}>
                                            {filteredItems.map((item, index) => {
                                                const imagePath = getImagePath(item, activeType);

                                                // Kainos logika atvaizdavimui
                                                let displayPrice = item.price;
                                                if (activeType === 1) {
                                                    displayPrice = item[mdfFinish];
                                                }

                                                return (
                                                    <CellItem
                                                        key={item.id || index}
                                                        image={imagePath}
                                                        title={item.code}
                                                        subTitle={item.name || `Kodas: ${item.code}`}
                                                        footer={displayPrice ? `${displayPrice} €/m²` : 'Kaina neteikiama'}
                                                        selected={selectedCode === item.code}
                                                        onClick={() => handleItemSelect(item)}
                                                        onView={() => handleItemView(item)}
                                                    />
                                                );
                                            })}
                                            {filteredItems.length === 0 && (
                                                <Text className={s.emptyResult}>Nieko nerasta pagal pasirinktus filtrus.</Text>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <Button size="large" appearance="outline" onClick={onClose}>Uždaryti</Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            {/* Item View Dialog */}
            <ItemViewDialog
                open={isViewOpen}
                onClose={() => setIsViewOpen(false)}
                item={viewItem}
                itemType={activeType === 1 ? 'mdf' : 'lmdp'}
            />
        </>
    );
};

export default PanelSelectingDialog;
